import { api } from '@orisini/ui/lib/api'

export interface MediaDto {
  id: string
  kind: 'image' | 'video'
  source: 'r2' | 'url' | 'local'
  filename: string | null
  mime: string | null
  sizeBytes: number | null
  width: number | null
  height: number | null
  durationMs: number | null
  status: 'pending' | 'ready' | 'error'
  error: string | null
  origin: string
  pageUrl: string | null
  localPath: string | null
  previewUrl: string | null
  createdAt: number
}

export interface PinDto {
  id: string
  accountId: string
  mediaId: string | null
  media: MediaDto | null
  boardId: string | null
  board: { id: string; name: string } | null
  title: string | null
  description: string | null
  link: string | null
  altText: string | null
  status: string
  onHold: boolean
  pinMode: 'auto' | 'fixed'
  queuePos: number | null
  scheduledAt: number | null
  attemptCount: number
  lastErrorCode: string | null
  lastErrorMessage: string | null
  postedAt: number | null
  pinterestPinUrl: string | null
  aiGenerated: boolean
  source: string
  /** Alasan pin belum layak masuk antrean; kosong berarti siap. */
  problems: string[]
  createdAt: number
}

export const mediaApi = {
  list: () => api.get<{ media: MediaDto[] }>('/media'),

  /**
   * Unggah file.
   *
   * Coba presigned PUT langsung ke penyimpanan dulu (melewati Worker, sehingga
   * file besar tidak terbentur batas body 100 MB). Kalau server belum
   * dikonfigurasi untuk itu, jatuh ke unggah lewat Worker.
   */
  async upload(file: File, onProgress?: (pct: number) => void): Promise<MediaDto> {
    try {
      const ticket = await api.post<{
        mediaId: string
        url: string
        headers: Record<string, string>
      }>('/media/upload-url', {
        filename: file.name,
        mime: file.type,
        sizeBytes: file.size,
      })

      await putWithProgress(ticket.url, file, ticket.headers, onProgress)
      const done = await api.post<{ media: MediaDto }>(`/media/${ticket.mediaId}/complete`)
      return done.media
    } catch (err) {
      const code = (err as { code?: string }).code
      if (code !== 'PRESIGN_UNAVAILABLE') throw err

      const form = new FormData()
      form.append('file', file)
      const res = await fetch('/api/media/direct', {
        method: 'POST',
        credentials: 'include',
        body: form,
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error?.message ?? 'Couldn\'t upload')
      onProgress?.(100)
      return (data as { media: MediaDto }).media
    }
  },

  fromUrl: (url: string, pageUrl?: string) =>
    api.post<{ media: MediaDto }>('/media/from-url', { url, pageUrl }),
  local: (path: string) => api.post<{ media: MediaDto }>('/media/local', { path }),
  remove: (id: string) => api.delete<{ ok: true }>(`/media/${id}`),
}

/** XHR dipakai (bukan fetch) semata-mata karena butuh event progress upload. */
function putWithProgress(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    // Hanya header yang ikut ditandatangani yang boleh dikirim.
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100))
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Storage rejected the upload (${xhr.status})`))
    xhr.onerror = () => reject(new Error('Connection to storage failed'))
    xhr.send(file)
  })
}

export const pinsApi = {
  list: (params: { accountId?: string; status?: string; limit?: number } = {}) => {
    const q = new URLSearchParams()
    if (params.accountId) q.set('accountId', params.accountId)
    if (params.status) q.set('status', params.status)
    if (params.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return api.get<{ pins: PinDto[] }>(`/pins${qs ? `?${qs}` : ''}`)
  },
  get: (id: string) => api.get<{ pin: PinDto }>(`/pins/${id}`),
  create: (body: Record<string, unknown>) => api.post<{ pin: PinDto }>('/pins', body),
  update: (id: string, patch: Record<string, unknown>) =>
    api.patch<{ pin: PinDto }>(`/pins/${id}`, patch),
  remove: (id: string) => api.delete<{ ok: true }>(`/pins/${id}`),

  // Transisi antrean — routenya ada di api/routes/queue.ts.
  enqueue: (id: string) => api.post<{ pin: PinDto }>(`/pins/${id}/enqueue`),
  dequeue: (id: string) => api.post<{ pin: PinDto }>(`/pins/${id}/dequeue`),
  /** Loncati antrean: dijadwalkan sekarang. Pacing akun tetap berlaku. */
  postNow: (id: string) => api.post<{ pin: PinDto }>(`/pins/${id}/post-now`),
  retry: (id: string) => api.post<{ pin: PinDto }>(`/pins/${id}/retry`),
}

/** Status yang masih boleh dimasukkan ke antrean. */
export const ENQUEUABLE_STATUSES = ['draft', 'cancelled'] as const
/** Status yang sudah di antrean dan bisa dipaksa tayang sekarang. */
export const POSTABLE_NOW_STATUSES = ['queued', 'scheduled'] as const
